import { Ref, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { analyticsUtils } from 'utils/analytics-utils';
import { storageSessionKey } from 'consts';
import { getConfiguratorURL } from 'common/store/selectors';

export const useFramePostMessage = ({
  ref,
  isFrameLoaded,
  customerId,
}: {
  ref: Ref<HTMLIFrameElement>;
  isFrameLoaded: boolean;
  customerId: string | null;
}) => {
  useEffect(() => {
    if (ref && isFrameLoaded) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ref?.current?.contentWindow.postMessage({ customerId }, window.location.origin);
    }
  }, [ref, isFrameLoaded, customerId]);
};

export const useLocalStorage = <T>(key: string, initialValue?: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });
  const setValue = (value: T) => {
    try {
      setStoredValue(value);
      if (!__IS_SERVER__) {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.log(error);
    }
  };
  return [storedValue, setValue] as const;
};

export const useAuthMessage = () => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [_, setSessionId] = useLocalStorage(storageSessionKey, null);
  const configuratorURL = useSelector(getConfiguratorURL);
  const [iframeState, refreshIframe] = useState({
    state: 'stale',
    queryParam: '',
  });

  const listener = useCallback(
    (event: MessageEvent) => {
      const ceramicURL = new URL(configuratorURL);
      const ceramicOrigin = ceramicURL.origin;
      if (event.origin !== ceramicOrigin) {
        return;
      }
      if (event.data.action === 'authorize') {
        document.dispatchEvent(new Event('form:auth:open'));
        setSessionId(event.data.sessionId);
      }
      if (event.data.action === 'GA') {
        analyticsUtils.sendEvent(`GA:${event.data.eventType}`, event.data.data.detail);
      }
      if (event.data.action === 'RefreshIFrame') {
        refreshIframe({
          state: 'refresh',
          queryParam: event.data.data,
        });
        setTimeout(() => {
          refreshIframe({
            state: 'stale',
            queryParam: '',
          });
        }, 1500);
      }
      if (event.data.action === 'RedirectWithParentOrigin') {
        window.open(`${event.data.data}`);
      }
    },
    [setSessionId, refreshIframe, configuratorURL]
  );

  useEffect(() => {
    window.addEventListener('message', listener);
    return () => {
      window.removeEventListener('message', listener);
    };
  }, [listener]);

  return {
    iframeState,
  };
};

export const useRefresh = ({
  iframeState,
  configuratorURL,
  setUrl,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  iframeState: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuratorURL: any;
  setUrl: (url: URL) => void;
}) => {
  useEffect(() => {
    if (iframeState.state === 'refresh') {
      const refreshURL = new URL(`${configuratorURL}${iframeState.queryParam}`);
      setUrl(refreshURL);
    }
  }, [configuratorURL, setUrl, iframeState.state, iframeState.queryParam]);
};
